import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/apiClient";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Section, Tile, Panel, MiniRow, CHART } from "@/components/dashboard/DashPrimitives";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend, Area, AreaChart,
} from "recharts";
import {
  TrendUp, TrendDown, Buildings, Receipt, Warning, Package, ShoppingCart,
  ClipboardText, Notebook, Coins, CurrencyInr, ChartLineUp, Wallet,
  ArrowUpRight, CheckCircle, ClockCountdown, ArrowRight, Fire,
  Wrench, ShieldCheck, ShieldWarning, Star, ChatCircle,
} from "@phosphor-icons/react";

/* ---------- Dashboard page ---------- */
export default function Dashboard() {
  const [d, setD] = useState(null);
  const [svc, setSvc] = useState(null);
  const [wh, setWh] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, w] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/service/summary"),
        api.get("/warehouses/summary"),
      ]);
      setD(a.data);
      setSvc(b.data);
      setWh(w.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const k = d?.kpis || {};
  const margin = useMemo(() => {
    return k.total_revenue > 0 ? ((k.profit / k.total_revenue) * 100).toFixed(1) : "0.0";
  }, [k.total_revenue, k.profit]);

  const collectionRatio = useMemo(() => {
    const invTotal = (k.total_revenue || 0) + (k.outstanding || 0);
    return invTotal > 0 ? ((k.total_revenue / invTotal) * 100).toFixed(1) : "0.0";
  }, [k.total_revenue, k.outstanding]);

  if (loading || !d) {
    return (
      <>
        <PageHeader title="Launchpad" subtitle="SAP Fiori · Operational Overview" module="Home" testid="dashboard-header" />
        <div className="p-8 text-slate-500 text-sm">Loading launchpad…</div>
      </>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Launchpad"
        subtitle="Enterprise operational overview across all modules"
        module="Home · Fiori"
        testid="dashboard-header"
        actions={
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <CheckCircle size={12} weight="fill" className="text-emerald-500" />
            Real-time · INR ₹
          </div>
        }
      />

      <div className="p-6 md:p-8">
        {/* ============================================================
             HEAD 1 · Financial Overview
        ============================================================ */}
        <Section
          code="01 · FI"
          title="Financial Overview"
          subtitle="Revenue, expenses, profit, and margin — company-wide"
          testid="section-financial"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile
              testid="kpi-revenue"
              label="Total Revenue"
              value={fmtMoney(k.total_revenue)}
              sub="Payments + Sales"
              trend="up"
              icon={CurrencyInr}
              to="/payments"
            />
            <Tile
              testid="kpi-expenses"
              label="Total Expenses"
              value={fmtMoney(k.total_expenses)}
              sub="Expenses + Purchases"
              trend="down"
              icon={Coins}
              to="/expenses"
            />
            <Tile
              testid="kpi-profit"
              label="Net Profit"
              value={fmtMoney(k.profit)}
              sub={k.profit >= 0 ? "In the black" : "Loss"}
              tone={k.profit >= 0 ? "accent" : "danger"}
              icon={ChartLineUp}
              to="/profit"
            />
            <Tile
              testid="kpi-margin"
              label="Profit Margin"
              value={`${margin}%`}
              sub="Profit ÷ Revenue"
              tone="orange"
              icon={TrendUp}
            />
          </div>

          {/* Financial charts nested inside FI section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
            <Panel title="Cash Flow · Revenue vs Expense" testid="panel-cashflow" className="lg:col-span-2">
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <AreaChart data={d.monthly}>
                    <defs>
                      <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0F172A" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#EA580C" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={10} />
                    <YAxis stroke="#64748B" fontSize={10} />
                    <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 11 }} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#0F172A" strokeWidth={2} fill="url(#gRev)" />
                    <Area type="monotone" dataKey="expense" stroke="#EA580C" strokeWidth={2} fill="url(#gExp)" />
                    <Line type="monotone" dataKey="profit" stroke="#0284C7" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Profit by Month" testid="panel-profit-bars">
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={d.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={10} />
                    <YAxis stroke="#64748B" fontSize={10} />
                    <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 11 }} />
                    <Bar dataKey="profit" fill="#EA580C" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </Section>

        {/* ============================================================
             HEAD 2 · Sales & Receivables
        ============================================================ */}
        <Section
          code="02 · SD/AR"
          title="Sales & Receivables"
          subtitle="Invoices, outstanding balance, aging and collections"
          testid="section-sales"
          action={
            <Link to="/invoices" className="text-[11.5px] text-[#EA580C] hover:underline font-semibold uppercase tracking-widest flex items-center gap-1">
              Open module <ArrowRight size={12} />
            </Link>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Tile
              testid="kpi-outstanding"
              label="Outstanding AR"
              value={fmtMoney(k.outstanding)}
              sub={`${k.unpaid_invoices} unpaid invoices`}
              icon={Wallet}
              tone={k.outstanding > 0 ? "warn" : "good"}
              to="/aging"
              dense
            />
            <Tile
              testid="kpi-overdue"
              label="Overdue"
              value={fmtMoney(k.overdue_amount)}
              sub={`${k.overdue_count} overdue invoices`}
              icon={Warning}
              tone={k.overdue_count > 0 ? "danger" : "good"}
              to="/aging"
              dense
            />
            <Tile
              testid="kpi-invoices"
              label="Invoices Issued"
              value={k.invoices_count}
              sub={`${k.unpaid_invoices} unpaid`}
              icon={Receipt}
              to="/invoices"
              dense
            />
            <Tile
              testid="kpi-collection"
              label="Collection Ratio"
              value={`${collectionRatio}%`}
              sub="Received ÷ Billed"
              icon={CheckCircle}
              tone="good"
              dense
            />
          </div>

          {/* Sub-panel: recent invoices */}
          <Panel
            title="Recent Invoices"
            testid="panel-recent-invoices"
            action={<Link to="/invoices" className="text-[11px] text-slate-500 hover:text-[#EA580C]">View all →</Link>}
          >
            {d.recent_invoices.length === 0 ? (
              <div className="text-[12px] text-slate-400 py-4 text-center">No invoices yet.</div>
            ) : (
              d.recent_invoices.map((i) => (
                <MiniRow
                  key={i.id}
                  testid={`recent-inv-${i.id}`}
                  left={<span>{i.invoice_number} · <span className="text-slate-500 font-normal">{i.client_name}</span></span>}
                  mid={`Due ${i.due_date || "—"}`}
                  right={fmtMoney(i.total)}
                  badge={<StatusBadge status={i.status} />}
                  to="/invoices"
                />
              ))
            )}
          </Panel>
        </Section>

        {/* ============================================================
             HEAD 3 · Procurement & Inventory
        ============================================================ */}
        <Section
          code="03 · MM"
          title="Procurement & Inventory"
          subtitle="Purchase orders, expenses, and site-wise stock health"
          testid="section-procurement"
          action={
            <Link to="/vendor-pos" className="text-[11.5px] text-[#EA580C] hover:underline font-semibold uppercase tracking-widest flex items-center gap-1">
              Open module <ArrowRight size={12} />
            </Link>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Tile
              testid="kpi-pos-open"
              label="Open POs"
              value={k.vendor_pos_open}
              sub={`${k.vendor_pos_count} total`}
              icon={ClipboardText}
              to="/vendor-pos"
              dense
            />
            <Tile
              testid="kpi-po-value"
              label="Open PO Value"
              value={fmtMoney(k.po_open_value)}
              sub="Committed spend"
              icon={ShoppingCart}
              to="/vendor-pos"
              dense
            />
            <Tile
              testid="kpi-inventory"
              label="Inventory Items"
              value={k.inventory_items_count}
              sub={`${k.low_stock_count} low-stock`}
              icon={Package}
              to="/inventory"
              tone={k.low_stock_count > 0 ? "warn" : "default"}
              dense
            />
            <Tile
              testid="kpi-purchases"
              label="Purchases (YTD)"
              value={k.purchase_count}
              sub={`${k.expense_count} expenses logged`}
              icon={Fire}
              to="/purchases"
              dense
            />
          </div>

          {/* Warehouse / Remote Store KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Tile
              testid="kpi-warehouses"
              label="Warehouses"
              value={wh?.warehouse_count ?? 0}
              sub="Remote stores active"
              icon={Buildings}
              to="/warehouses"
              dense
            />
            <Tile
              testid="kpi-stock-value"
              label="Stock Value"
              value={fmtMoney(wh?.total_stock_value || 0)}
              sub={`${wh?.item_count || 0} SKUs on hand`}
              icon={Package}
              to="/warehouses"
              dense
            />
            <Tile
              testid="kpi-wh-low-stock"
              label="Low Stock (Store)"
              value={wh?.low_stock_count ?? 0}
              sub="Below reorder level"
              icon={Warning}
              to="/warehouses"
              tone={wh?.low_stock_count ? "warn" : "default"}
              dense
            />
            <Tile
              testid="kpi-transfers-pending"
              label="Pending Transfers"
              value={wh?.pending_transfers ?? 0}
              sub="Awaiting dispatch"
              icon={ArrowUpRight}
              to="/stock-transfers"
              dense
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Panel
              title="Recent Vendor POs"
              testid="panel-recent-pos"
              action={<Link to="/vendor-pos" className="text-[11px] text-slate-500 hover:text-[#EA580C]">View all →</Link>}
            >
              {d.recent_pos.length === 0 ? (
                <div className="text-[12px] text-slate-400 py-4 text-center">No POs raised yet.</div>
              ) : (
                d.recent_pos.map((v) => (
                  <MiniRow
                    key={v.id}
                    testid={`recent-po-${v.id}`}
                    left={<span>{v.po_number} · <span className="text-slate-500 font-normal">{v.vendor_name}</span></span>}
                    right={fmtMoney(v.total)}
                    badge={<StatusBadge status={v.status || "draft"} />}
                    to="/vendor-pos"
                  />
                ))
              )}
            </Panel>

            <Panel title="Expenses by Category" testid="panel-expense-cat">
              {d.expense_by_category.length === 0 ? (
                <div className="text-[12px] text-slate-400 py-6 text-center">No expenses yet.</div>
              ) : (
                <div style={{ width: "100%", height: 180 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={d.expense_by_category}
                        dataKey="amount"
                        nameKey="category"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={1}
                      >
                        {d.expense_by_category.map((entry, i) => (
                          <Cell key={entry.category} fill={CHART[i % CHART.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 11 }} />
                      <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          </div>
        </Section>

        {/* ============================================================
             HEAD 4 · Projects (Project System)
        ============================================================ */}
        <Section
          code="04 · PS"
          title="Projects & Operations"
          subtitle="Active portfolio, meetings, and delivery pipeline"
          testid="section-projects"
          action={
            <Link to="/projects" className="text-[11.5px] text-[#EA580C] hover:underline font-semibold uppercase tracking-widest flex items-center gap-1">
              Open module <ArrowRight size={12} />
            </Link>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Tile
              testid="kpi-projects"
              label="Total Projects"
              value={k.projects_count}
              sub={`${k.active_projects} active · ${k.completed_projects} done`}
              icon={Buildings}
              to="/projects"
              dense
            />
            <Tile
              testid="kpi-active"
              label="Active"
              value={k.active_projects}
              sub="In progress"
              icon={Fire}
              tone="orange"
              to="/projects"
              dense
            />
            <Tile
              testid="kpi-meetings"
              label="Meetings / MOM"
              value={k.meetings_count}
              sub={`${k.meetings_upcoming} upcoming`}
              icon={Notebook}
              to="/meetings"
              dense
            />
            <Tile
              testid="kpi-payments"
              label="Payments Logged"
              value={k.payments_count}
              sub="Collections recorded"
              icon={CurrencyInr}
              to="/payments"
              dense
            />
          </div>

          <Panel
            title="Recent Projects"
            testid="panel-recent-projects"
            action={<Link to="/projects" className="text-[11px] text-slate-500 hover:text-[#EA580C]">View all →</Link>}
          >
            {d.recent_projects.length === 0 ? (
              <div className="text-[12px] text-slate-400 py-4 text-center">No projects yet.</div>
            ) : (
              d.recent_projects.map((p) => (
                <MiniRow
                  key={p.id}
                  testid={`recent-proj-${p.id}`}
                  left={<span>{p.name} · <span className="text-slate-500 font-normal">{p.client_name}</span></span>}
                  right={fmtMoney(p.value || 0)}
                  badge={<StatusBadge status={p.status || "planning"} />}
                  to={`/projects/${p.id}`}
                />
              ))
            )}
          </Panel>
        </Section>

        {/* ============================================================
             HEAD 5 · Service & Support (SV)
        ============================================================ */}
        <Section
          code="05 · SV"
          title="Service & Support"
          subtitle="Equipment, snags, tickets, PPM and warranty across the fleet"
          testid="section-service"
          action={
            <Link to="/service/equipment" className="text-[11.5px] text-[#EA580C] hover:underline font-semibold uppercase tracking-widest flex items-center gap-1">
              Open module <ArrowRight size={12} />
            </Link>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Tile
              testid="kpi-equipment"
              label="Equipment"
              value={svc?.equipment_count ?? 0}
              sub="Chillers · AHU · VRF · Pumps"
              icon={Wrench}
              to="/service/equipment"
              dense
            />
            <Tile
              testid="kpi-open-tickets"
              label="Open Tickets"
              value={svc?.open_tickets ?? 0}
              sub={`${svc?.high_priority_tickets || 0} high-priority`}
              icon={ChatCircle}
              to="/service/tickets"
              tone={svc?.high_priority_tickets ? "warn" : "default"}
              dense
            />
            <Tile
              testid="kpi-open-snags"
              label="Open Snags"
              value={svc?.open_snags ?? 0}
              sub={`${svc?.p1_snags || 0} P1 · critical`}
              icon={Warning}
              to="/service/snags"
              tone={svc?.p1_snags ? "danger" : "default"}
              dense
            />
            <Tile
              testid="kpi-warranty"
              label="Warranty"
              value={`${svc?.warranty_expiring ?? 0}`}
              sub={`${svc?.warranty_expired || 0} expired · expiring in 60d`}
              icon={ShieldWarning}
              to="/service/warranty"
              tone={svc?.warranty_expiring || svc?.warranty_expired ? "warn" : "good"}
              dense
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile
              testid="kpi-ppm-active"
              label="PPM Active"
              value={svc?.ppm_active ?? 0}
              sub={`${svc?.ppm_due_soon || 0} due within 30d`}
              icon={ClockCountdown}
              to="/service/ppm"
              dense
            />
            <Tile
              testid="kpi-commissioning"
              label="Commissioning · Signed"
              value={svc?.reports_signed ?? 0}
              sub="Handover-ready"
              icon={CheckCircle}
              to="/service/commissioning"
              tone="good"
              dense
            />
            <Tile
              testid="kpi-p1-snags"
              label="P1 Snags"
              value={svc?.p1_snags ?? 0}
              sub="Critical punch items"
              icon={Fire}
              to="/service/snags"
              tone={svc?.p1_snags ? "danger" : "default"}
              dense
            />
            <Tile
              testid="kpi-csat"
              label="CSAT · NPS"
              value="—"
              sub="Client feedback"
              icon={Star}
              to="/service/csat"
              tone="orange"
              dense
            />
          </div>
        </Section>
      </div>
    </div>
  );
}
